/**
 * Operations — the Daily Control Tower, in five sections.
 *
 * This is a port of the published report, section for section: Control tower,
 * Plan &amp; forecast, Cities, Sub-lines, Fill rate. Every page opens with the
 * horizon banner, every figure states its basis, and the drill-down modals are a
 * STACK so a reader who went three levels deep does not lose the two above.
 *
 * TWO CONTROLS ARE THE PAGE'S OWN, and they sit under the top bar rather than
 * inside a card, because they change every figure below them:
 *
 *  · the MRP / SP basis toggle, hidden on Fill rate — a fill rate is a unit
 *    ratio with no rupee in it, so a basis toggle there would be a control that
 *    changes nothing;
 *  · nothing else. The window belongs to the shared top bar, so Operations and
 *    the CEO pages cannot be read on two different periods by accident.
 */
import { useState } from 'react';
import { ReportShell } from '../ReportShell';
import { CitiesCard } from './CitiesCard';
import { ControlTower } from './ControlTower';
import { FillRateCard } from './FillRateCard';
import { OpsBanner } from './OpsBanner';
import { PlanForecast } from './PlanForecast';
import { SubLinesCard } from './SubLinesCard';
import { useReport } from '../useReport';
import { type Basis, BasisToggle, DrillHost } from './opsLib';
import { loadActiveProfile } from '@/lib/auth';
import { DEFAULT_RANGE, type DateRange } from '@/lib/dateRange';
import type { Route } from '@/lib/route';

const TITLES: Record<string, string> = {
  'control-tower': 'Control tower',
  'plan-forecast': 'Plan & forecast',
  cities: 'Cities',
  'sub-lines': 'Sub-lines',
  'fill-rate': 'Fill rate',
};

export function OpsView({
  route, onNavigate, onSignOut,
}: { route: Route; onNavigate: (s: string, p: string) => void; onSignOut?: () => void }) {
  const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);
  // MRP is the default because it is the basis the plan is written on, and the
  // plan is what the operations team is judged against.
  const [basis, setBasis] = useState<Basis>('mrp');
  const customer = loadActiveProfile() ?? '';
  const page = TITLES[route.page] ? route.page : 'control-tower';
  // The anchor query only supplies the resolved window to the top bar; each
  // page reads its own data (and shares this one from the query cache).
  const anchor = useReport('/v1/reports/ops/control-tower', customer, range);

  return (
    <ReportShell
      title={`Operations — ${TITLES[page] ?? 'Control tower'}`}
      route={{ section: 'operations', page }}
      onNavigate={onNavigate}
      range={range}
      onRangeChange={setRange}
      {...(anchor.data?.window ? { resolved: anchor.data.window } : {})}
      {...(onSignOut ? { onSignOut } : {})}
      filters={page === 'fill-rate' ? undefined : (
        <div className="rds" style={{ padding: '0 24px', display: 'flex', gap: 10, alignItems: 'center' }}>
          <BasisToggle value={basis} onChange={setBasis} />
          <span className="asof">
            GMV only. ASP, units, DRR, OSA and discount do not move with the basis.
          </span>
        </div>
      )}
    >
      <DrillHost>
        <OpsBanner customer={customer} range={range} basis={basis} page={page} />
        {page === 'control-tower' && <ControlTower customer={customer} range={range} basis={basis} />}
        {page === 'plan-forecast' && <PlanForecast customer={customer} range={range} basis={basis} />}
        {page === 'cities' && <CitiesCard customer={customer} range={range} basis={basis} />}
        {page === 'sub-lines' && <SubLinesCard customer={customer} range={range} basis={basis} />}
        {page === 'fill-rate' && <FillRateCard customer={customer} range={range} />}
      </DrillHost>
    </ReportShell>
  );
}
